import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl, defineMessages} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    useCustomer,
    useCustomerId,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {} from '@salesforce/commerce-sdk-react'

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

import {
    Box,
    Heading,
    Text,
    Stack,
    Flex,
    Button,
    useDisclosure,
    Alert,
    AlertIcon
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Divider} from '@chakra-ui/react'
import Field from '../../components/field'
import {InfoCircleBlack} from '../../components/icons'
import useCommunicationsFields from '../../components/forms/useCommunicationsFields'
import SMSOptInModal from './sms-optin-modal'
import {useForm} from 'react-hook-form'
import {sendViewErrorEvent} from '../../utils/gtm-utils'
import usedDeviceDetection from '../../hooks/use-device-detection'
import SMSOptIn from './sms-optin'
import {useBrand} from '../../contexts/brand-context'
import {useCustomToast} from '../../contexts/custom-toast-context'

const FALLBACK_LANGUAGE_PREFERENCE = 'fr'

const EMAIL_OPT_IN_KEYS = {
    garage: {
        promotional: 'c_email_garage_optedIn',
        personalized: 'c_email_garage_personalized_optedIn'
    },
    dynamite: {
        promotional: 'c_email_dynamite_optedIn',
        personalized: 'c_email_dynamite_personalized_optedIn'
    }
}

const getEmailOptInKeys = (isGarage) =>
    isGarage ? EMAIL_OPT_IN_KEYS.garage : EMAIL_OPT_IN_KEYS.dynamite

const CommunicationPreferences = ({prefix = ''}) => {
    const {formatMessage} = useIntl()
    const {isGarage} = useBrand()
    const {showCustomToast} = useCustomToast()
    const {isOpen, onOpen, onClose} = useDisclosure()

    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const SMS_OPTIN_LOYALTY_ENABLED = getSiteEnvVarValue(config.app, 'SMS_OPTIN_LOYALTY_ENABLED', siteId)
    const SFSC_EMAIL_SUBSCRIPTION_ENABLED = getSiteEnvVarValue(config.app, 'SFSC_EMAIL_SUBSCRIPTION_ENABLED', siteId)
    const isSMSOptInEnabled = SMS_OPTIN_LOYALTY_ENABLED === 'true'
    const isSfscEmailSubscriptionEnabled = SFSC_EMAIL_SUBSCRIPTION_ENABLED == 'true'
    const customerId = useCustomerId()
    const {data: customer} = useCustomer({parameters: {customerId}}, {enabled: !!customerId})
    const {mutate: updateEmailPreferences, isLoading} =
        useShopperCustomersMutation('updateCustomer')

    const {
        mutate: updateAttentiveSMS,
        isLoading: isSmsLoading,
        isSuccess: isSmsSuccess
    } = useShopperCustomersMutation('updateCustomer')

    const form = useForm({
        defaultValues: {
            [`${prefix}emailPromotionalOptIn`]: false,
            [`${prefix}emailPersonalizedOptIn`]: false,
            [`${prefix}languagePreference`]: FALLBACK_LANGUAGE_PREFERENCE
        }
    })

    const emailPromotionalOptIn = form.watch(`${prefix}emailPromotionalOptIn`)

    useEffect(() => {
        if (!emailPromotionalOptIn) {
            form.setValue(`${prefix}emailPersonalizedOptIn`, false)
        }
    }, [emailPromotionalOptIn])

    const fields = useCommunicationsFields({form, prefix, emailPromotionalOptIn})
    const emailKeys = getEmailOptInKeys(isGarage)

    const headingRef = useRef()

    const device = usedDeviceDetection()

    useEffect(() => {
        headingRef?.current?.focus()
    }, [])

    useEffect(() => {
        if (customer) {
            form.reset({
                [`${prefix}emailPromotionalOptIn`]: customer[emailKeys.promotional] ?? false,
                [`${prefix}emailPersonalizedOptIn`]: customer[emailKeys.personalized] ?? false,
                [`${prefix}languagePreference`]:
                    customer.c_gigya_language || FALLBACK_LANGUAGE_PREFERENCE
            })
        }
    }, [customer, emailKeys.personalized, emailKeys.promotional, prefix, form])

    const onSubmit = ({emailPromotionalOptIn, emailPersonalizedOptIn, languagePreference}) => {
        updateEmailPreferences(
            {
                parameters: {
                    customerId
                },
                body: {
                    [emailKeys.promotional]: emailPromotionalOptIn,
                    [emailKeys.personalized]: emailPersonalizedOptIn,
                    c_gigya_language: languagePreference,
                    c_device: device
                }
            },
            {
                onSuccess() {
                    showCustomToast({
                        message: formatMessage({
                            id: 'communication_preferences.update.success_message',
                            defaultMessage: 'Communication preferences updated'
                        })
                    })
                },
                onError() {
                    const errorMessage = isGarage
                        ? formatMessage({
                              id: 'communication_preferences.email.error_message.garage',
                              defaultMessage:
                                  'Failed to update email preferences. Please try again.'
                          })
                        : formatMessage({
                              id: 'communication_preferences.email.error_message.dynamite',
                              defaultMessage:
                                  'Failed to update email preferences. Please try again.'
                          })
                    sendViewErrorEvent(errorMessage)
                    showCustomToast({message: errorMessage})
                }
            }
        )
    }

    return (
        <Box mx="auto">
            <Stack spacing={6} width={{base: 'full', lg: '458px'}}>
                <Text
                    alignItems="center"
                    as="h1"
                    textStyle="webH1"
                    tabIndex="0"
                    ref={headingRef}
                    gap={6}
                >
                    <FormattedMessage
                        id="account_detail.communication_preferences"
                        defaultMessage="Communication Preferences"
                    />
                </Text>

                {form.formState.errors?.global && (
                    <Alert status="error" maxW={'max-content'}>
                        <AlertIcon color="red.500" boxSize={4} />
                        <Text fontSize="sm" ml={3}>
                            {form.formState.errors.global.message}
                        </Text>
                    </Alert>
                )}

                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Stack spacing={{base: '12', lg: '16'}}>
                        {/* Transactional email notice */}
                        <Box
                            display={'flex'}
                            flexDirection={'column'}
                            gap={'12px'}
                            data-testid="communication-transactional-email-notice"
                        >
                            <Flex gap={'8px'} data-testid="communication-transactional-email-title">
                                <InfoCircleBlack boxSize={'20px'} />
                                <Text fontSize={{base: 'xs', md: 'sm'}} fontWeight={'500'}>
                                    <FormattedMessage
                                        id="account_detail.transactional_emails_cannot_unsubscribe"
                                        defaultMessage="Transactional emails can’t be unsubscribed"
                                    />
                                </Text>
                            </Flex>
                            <Text
                                fontSize={'xs'}
                                fontWeight={'400'}
                                data-testid="communication-transactional-email-description"
                            >
                                {isGarage ? (
                                    <FormattedMessage
                                        id="account_detail.transactional_emails_unsubscribe_note.garage"
                                        defaultMessage="You cannot unsubscribe from transactional emails (including order confirmation or cancellation emails)."
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="account_detail.transactional_emails_unsubscribe_note.dynamite"
                                        defaultMessage="You cannot unsubscribe from transactional emails (including order confirmation or cancellation emails)."
                                    />
                                )}
                            </Text>
                        </Box>

                        {/* Language preference section */}
                        <Box
                            display={'flex'}
                            flexDirection={'column'}
                            gap={'16px'}
                            data-testid="communication-language-section"
                        >
                            <Heading
                                as="h3"
                                fontSize={{base: 'xs', lg: 'sm'}}
                                fontWeight={'700'}
                                textTransform={'uppercase'}
                                data-testid="communication-language-title"
                            >
                                <FormattedMessage
                                    id="account_detail.language_label"
                                    defaultMessage="Language"
                                />
                            </Heading>
                            <Box
                                display={'flex'}
                                flexDirection={'column'}
                                gap={'8px'}
                                data-testid="communication-language-options"
                            >
                                <Field
                                    {...fields.languagePreference}
                                    labelProps={{
                                        fontSize: '14px',
                                        fontWeight: '400',
                                        ml: 0
                                    }}
                                    radioGroupProps={{
                                        spacing: 2
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Email communication preferences section */}
                        {isSfscEmailSubscriptionEnabled && (
                            <Box
                                display={'flex'}
                                flexDirection={'column'}
                                gap={'16px'}
                                data-testid="communication-email-section"
                            >
                                <Heading
                                    as="h3"
                                    fontSize={{base: 'xs', lg: 'sm'}}
                                    fontWeight={'700'}
                                    textTransform={'uppercase'}
                                    data-testid="communication-email-title"
                                >
                                    <FormattedMessage
                                        id="account_detail.email_notifications_label"
                                        defaultMessage="Email Notifications"
                                    />
                                </Heading>
                                <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                                    <Flex flexDirection={'column'} gap={'16px'}>
                                        <Text
                                            fontSize={'sm'}
                                            fontWeight={'400'}
                                            display={'flex'}
                                            flexDirection={'column'}
                                            data-testid="communication-email-general-description"
                                        >
                                            <Text as="span">
                                                <FormattedMessage
                                                    id="communication_preferences.promotional_text"
                                                    defaultMessage="Promotional"
                                                />
                                            </Text>
                                            <Text as="span">
                                                {isGarage ? (
                                                    <FormattedMessage
                                                        id="communication_preferences.email.general.description.garage"
                                                        defaultMessage="Enjoy our new arrivals, exclusive offers, sales, and more."
                                                    />
                                                ) : (
                                                    <FormattedMessage
                                                        id="communication_preferences.email.general.description.dynamite"
                                                        defaultMessage="Enjoy our new arrivals, exclusive offers, sales, and more."
                                                    />
                                                )}
                                            </Text>
                                        </Text>
                                        <Field
                                            {...fields.emailPromotionalOptIn}
                                            data-testid="communication-email-general-switch"
                                        />
                                        <Field
                                            {...fields.emailPersonalizedOptIn}
                                            data-testid="communication-email-general-checkbox"
                                        />
                                    </Flex>
                                </Box>
                            </Box>
                        )}

                        <Box>
                            <Button
                                data-testid="communication-button-save-changes"
                                variant="solid"
                                type="submit"
                                fontSize="sm"
                                fontWeight="700"
                                textTransform="uppercase"
                                width={{base: 'full', md: 'auto'}}
                                minWidth={{base: 'full', md: '224px'}}
                                height={'48px'}
                                onClick={() => {
                                    form.clearErrors('global')
                                }}
                                isLoading={isLoading}
                                loadingText={formatMessage({
                                    id: 'communication_preferences.sms.saving',
                                    defaultMessage: 'Saving...'
                                })}
                            >
                                <FormattedMessage
                                    id="communication_preferences.save_changes"
                                    defaultMessage="Save Changes"
                                />
                            </Button>
                        </Box>
                        <Divider
                            width={{base: 'full', lg: '458px'}}
                            height="1px"
                            bg="#ECE9E9"
                            data-testid="communication-email-divider"
                        />
                    </Stack>
                </form>
            </Stack>
            {isSMSOptInEnabled && <SMSOptIn />}
            {/* Removing for MVP */}
            {/* {isSMSOptInEnabled && <SMSOptInModal isOpen={isOpen} onClose={onClose} />} */}
        </Box>
    )
}

CommunicationPreferences.propTypes = {
    prefix: PropTypes.string
}

export default CommunicationPreferences
